import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';
import { Plus, HelpCircle } from 'lucide-react';

const COPY = {
  es: {
    kicker: 'Dudas frecuentes',
    title: 'Preguntas frecuentes',
    items: [
      {
        q: '¿Las clases son individuales o en grupo?',
        a: 'Todas mis clases son individuales, de profesora y alumno. Así aprovechas todo el tiempo para hablar y avanzamos exactamente por donde tú necesitas.',
      },
      {
        q: '¿Qué nivel necesito para empezar?',
        a: 'Ninguno. Puedes empezar desde cero o desde cualquier nivel entre A1 y C2. En la clase de prueba valoramos tu punto de partida y, si quieres, puedes hacer antes el test de nivel gratuito de la web.',
      },
      {
        q: '¿Puedo preparar el examen DELE contigo?',
        a: 'Sí. Soy examinadora DELE acreditada y preparo las cuatro pruebas del examen con simulacros, corrección detallada y estrategias para el día del examen. También preparo el SIELE.',
      },
      {
        q: '¿Cómo son las clases por Zoom o Google Meet?',
        a: 'La clase es una videollamada de 50 minutos (30 en la clase de prueba). Recibes el enlace por email al confirmar la reserva, solo necesitas conexión, auriculares y ganas de hablar. Trabajamos con pizarra compartida y documentos en pantalla.',
      },
      {
        q: '¿Hay que comprar libros o materiales?',
        a: 'No hace falta. Yo preparo y te envío todo el material: fichas, audios, lecturas y ejercicios, adaptados a cada clase. En la sección de Recursos también tienes material gratuito para descargar.',
      },
      {
        q: '¿La clase de prueba es realmente gratis?',
        a: 'Sí, dura 30 minutos, es online y sin compromiso. Nos conocemos, valoramos tu nivel y definimos un plan. Si decides seguir, reservamos la primera clase completa.',
      },
      {
        q: '¿Qué pasa si no puedo asistir a una clase?',
        a: 'Puedes cambiar o cancelar tu clase desde el enlace del email de confirmación hasta 24 horas antes, sin coste. Así buscamos otro hueco que te venga bien.',
      },
    ],
  },
  en: {
    kicker: 'Common questions',
    title: 'Frequently asked questions',
    items: [
      { q: 'Are the lessons one-to-one or in a group?', a: 'All my lessons are one-to-one, just teacher and student. That way you use the whole session to speak and we move forward exactly where you need it.' },
      { q: 'What level do I need to start?', a: 'None. You can start from scratch or from any level between A1 and C2. In the trial lesson we assess your starting point, and you can also take the free level test on this website beforehand.' },
      { q: 'Can I prepare the DELE exam with you?', a: 'Yes. I am an accredited DELE examiner and I prepare all four parts of the exam with mock tests, detailed feedback and strategies for exam day. I also prepare the SIELE.' },
      { q: 'What are lessons on Zoom or Google Meet like?', a: 'The lesson is a 50-minute video call (30 minutes for the trial). You get the link by email when your booking is confirmed; you only need a connection, headphones and the will to speak. We use a shared whiteboard and documents on screen.' },
      { q: 'Do I need to buy books or materials?', a: 'No. I prepare and send you all the material: worksheets, audio, readings and exercises, adapted to each lesson. In the Resources section you also have free material to download.' },
      { q: 'Is the trial lesson really free?', a: 'Yes, it lasts 30 minutes, it is online and with no commitment. We get to know each other, assess your level and define a plan. If you decide to continue, we book your first full lesson.' },
      { q: 'What if I cannot attend a lesson?', a: 'You can reschedule or cancel from the link in your confirmation email up to 24 hours in advance, free of charge. We then find another slot that suits you.' },
    ],
  },
  de: {
    kicker: 'Häufige Fragen',
    title: 'Häufig gestellte Fragen',
    items: [
      { q: 'Ist der Unterricht einzeln oder in der Gruppe?', a: 'Mein Unterricht findet immer einzeln statt, nur Lehrerin und Lernende. So nutzt du die ganze Zeit zum Sprechen und wir arbeiten genau dort, wo du es brauchst.' },
      { q: 'Welches Niveau brauche ich zum Start?', a: 'Keines. Du kannst bei Null anfangen oder auf jedem Niveau zwischen A1 und C2 einsteigen. In der Probestunde schauen wir deinen Startpunkt an; vorher kannst du auch den kostenlosen Einstufungstest auf der Website machen.' },
      { q: 'Kann ich mit dir die DELE-Prüfung vorbereiten?', a: 'Ja. Ich bin akkreditierte DELE-Prüferin und bereite alle vier Prüfungsteile mit Probeprüfungen, ausführlicher Korrektur und Strategien für den Prüfungstag vor. Auch SIELE ist möglich.' },
      { q: 'Wie läuft der Unterricht über Zoom oder Google Meet?', a: 'Der Unterricht ist ein Videoanruf von 50 Minuten (30 Minuten in der Probestunde). Den Link bekommst du per E-Mail mit der Buchungsbestätigung; du brauchst nur Internet, Kopfhörer und Lust zu sprechen. Wir nutzen ein gemeinsames Whiteboard und Dokumente auf dem Bildschirm.' },
      { q: 'Muss ich Bücher oder Materialien kaufen?', a: 'Nein. Ich erstelle und schicke dir alle Materialien: Arbeitsblätter, Audios, Lesetexte und Übungen, passend zu jeder Stunde. Im Bereich Ressourcen gibt es zusätzlich kostenloses Material zum Download.' },
      { q: 'Ist die Probestunde wirklich kostenlos?', a: 'Ja, sie dauert 30 Minuten, ist online und ohne Verpflichtung. Wir lernen uns kennen, schauen dein Niveau an und legen einen Plan fest. Wenn du weitermachen möchtest, buchen wir die erste komplette Stunde.' },
      { q: 'Was passiert, wenn ich eine Stunde nicht wahrnehmen kann?', a: 'Du kannst über den Link in der Bestätigungs-E-Mail bis 24 Stunden vorher kostenlos verschieben oder absagen. Dann suchen wir einen neuen Termin, der dir passt.' },
    ],
  },
};

export const HomeFaq = () => {
  const { language } = useLanguage();
  const copy = COPY[language] || COPY.es;
  const [open, setOpen] = useState(0);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: copy.items.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: { '@type': 'Answer', text: item.a },
    })),
  };

  return (
    <section id="faq" className="py-20 md:py-24 bg-gradient-to-b from-white to-[#E8F5FA]" data-testid="home-faq-section">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div ref={ref} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 text-[#F5A6A4] font-semibold text-xs uppercase tracking-[0.18em]">
            <HelpCircle className="w-4 h-4" />
            {copy.kicker}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#4A4A4A] mt-4 mb-6" data-testid="home-faq-title">
            {copy.title}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mx-auto" />
        </motion.div>

        <div className="space-y-3">
          {copy.items.map((item, i) => {
            const isOpen = open === i;
            return (
              <motion.div
                key={item.q}
                initial={{ opacity: 0, y: 16 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.08 * i }}
                className={`rounded-2xl bg-white border transition-colors ${isOpen ? 'border-[#7DBDD9] shadow-lg' : 'border-gray-100 shadow-sm'}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  data-testid={`home-faq-question-${i}`}
                  className="w-full flex items-center justify-between gap-4 text-left px-6 py-5"
                >
                  <h3 className="text-base font-semibold text-[#4A4A4A]">{item.q}</h3>
                  <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? 'bg-[#7DBDD9] rotate-45' : 'bg-[#E8F5FA]'}`}>
                    <Plus className={`w-4 h-4 ${isOpen ? 'text-white' : 'text-[#7DBDD9]'}`} />
                  </span>
                </button>
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p
                        className="px-6 pb-6 text-sm text-gray-600 leading-relaxed"
                        data-testid={`home-faq-answer-${i}`}
                      >
                        {item.a}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
